/**
 * Consistent date formatting utilities for the entire app
 * Standard format: MM/DD/YY (e.g., 12/03/25)
 * 
 * TIMEZONE HANDLING:
 * - Customer-facing: Display in device timezone
 * - Vendor-facing: Display in vendor's stored timezone
 */

/**
 * Get customer's device timezone
 * @returns {string} IANA timezone string
 */
export const getCustomerTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format date to MM/DD/YY
 * Handles date-only strings (YYYY-MM-DD) specially to avoid timezone issues.
 * When input is a date-only string, it extracts the date parts directly
 * to prevent browser timezone conversion causing off-by-one day errors.
 * 
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date (e.g., "12/03/25")
 */
export const formatShortDate = (date) => {
  if (!date) return '';
  
  // Handle date-only strings (YYYY-MM-DD) directly to avoid timezone issues
  // JavaScript's new Date('2026-03-15') treats it as UTC midnight,
  // which can show as March 14 in US timezones when converted to local
  if (typeof date === 'string') {
    // Check if it's a date-only format (YYYY-MM-DD)
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return `${month}/${day}/${year.slice(-2)}`;
    }
    
    // Also handle ISO date strings by extracting the date portion
    // e.g., "2026-03-15T12:00:00.000Z" -> extract "2026-03-15"
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${month}/${day}/${year.slice(-2)}`;
    }
  }
  
  // For Date objects and other formats, use the original logic
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  
  return `${month}/${day}/${year}`;
};

/**
 * Format time to HH:MM AM/PM
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted time (e.g., "06:43 PM")
 */
export const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  return d.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * Format date and time together
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date and time (e.g., "12/03/25 • 06:43 PM")
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  return `${formatShortDate(date)} • ${formatTime(date)}`;
};

/**
 * Format date in long form for display (e.g., "December 3, 2025")
 * Handles date-only strings specially to avoid timezone issues.
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date
 */
export const formatLongDate = (date) => {
  if (!date) return '';
  
  // Handle date-only strings (YYYY-MM-DD) directly to avoid timezone issues
  if (typeof date === 'string') {
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      // Create date at noon UTC to avoid timezone shifts
      const d = new Date(`${year}-${month}-${day}T12:00:00Z`);
      return d.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric',
        timeZone: 'UTC'  // Use UTC to match the noon UTC time
      });
    }
  }
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  return d.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

/**
 * Format time in a specific timezone
 * @param {string|Date} date - Date string or Date object (UTC)
 * @param {string} timezone - IANA timezone string (e.g., "America/Chicago")
 * @returns {string} Formatted time in the specified timezone
 */
export const formatTimeInTimezone = (date, timezone) => {
  if (!date || !timezone) return formatTime(date);
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  try {
    return d.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: timezone
    });
  } catch (e) {
    // Fallback to local time if timezone is invalid
    return formatTime(date);
  }
};

/**
 * Format date and time showing both customer's local time and vendor's timezone
 * For use on booking confirmation pages
 * @param {string|Date} date - Date string or Date object (UTC)
 * @param {string} vendorTimezone - Vendor's IANA timezone string
 * @returns {object} { localTime, vendorTime, vendorTimezoneLabel }
 */
export const formatBookingDateWithVendorTime = (date, vendorTimezone) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  
  const customerTimezone = getCustomerTimezone();
  
  const localTime = d.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    timeZone: customerTimezone
  });
  
  let vendorTime = localTime;
  let vendorTimezoneLabel = vendorTimezone;
  
  if (vendorTimezone) {
    try {
      vendorTime = d.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: vendorTimezone
      });
      // Format timezone label (e.g., "America/Chicago" -> "Chicago")
      vendorTimezoneLabel = vendorTimezone.split('/').pop().replace(/_/g, ' ');
    } catch (e) {
      // Use local time if vendor timezone is invalid
    }
  }
  
  return {
    localTime,
    vendorTime,
    vendorTimezoneLabel,
    showVendorTime: vendorTimezone && vendorTime !== localTime
  };
};

/**
 * Whole calendar days remaining until an event date.
 *
 * Canonical countdown helper — single source of truth for every "X days to go"
 * surface (Home hero, Event Command Center, Events list, etc.). Treats event
 * dates as *calendar days* in the user's local timezone (midnight-to-midnight),
 * matching how customers think about countdowns: "the wedding is in 35 days"
 * doesn't change at 11:59pm vs 12:01am.
 *
 * Handles the YYYY-MM-DD → UTC-midnight trap by parsing date-only strings as
 * LOCAL midnight, identical to the approach used by formatShortDate / formatLongDate.
 *
 * @param {string|Date} eventDate - "YYYY-MM-DD", ISO timestamp, or Date object
 * @returns {number} whole-days remaining. Negative if past. Returns Infinity
 *   for missing/invalid input so callers can do daysUntil >= 0 / daysUntil < N
 *   checks without crashing.
 *
 * Examples (assuming "today" = 2026-04-25):
 *   getDaysUntilEvent("2026-05-30")             -> 35
 *   getDaysUntilEvent("2026-04-25")             -> 0
 *   getDaysUntilEvent("2026-04-26")             -> 1
 *   getDaysUntilEvent("2026-04-24")             -> -1
 *   getDaysUntilEvent("2026-05-30T17:00:00Z")   -> 35  (any time-of-day → same day count)
 *   getDaysUntilEvent(null)                     -> Infinity
 */
export const getDaysUntilEvent = (eventDate) => {
  if (!eventDate) return Infinity;

  let target;
  if (typeof eventDate === 'string') {
    // Date-only ("YYYY-MM-DD") — parse parts directly and build LOCAL midnight
    const dateOnlyMatch = eventDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      target = new Date(Number(y), Number(m) - 1, Number(d));
    } else {
      // ISO timestamp ("YYYY-MM-DDTHH:..."): extract the date portion and
      // anchor to LOCAL midnight, so a Z-timestamp doesn't drift across days.
      const isoMatch = eventDate.match(/^(\d{4})-(\d{2})-(\d{2})T/);
      if (isoMatch) {
        const [, y, m, d] = isoMatch;
        target = new Date(Number(y), Number(m) - 1, Number(d));
      } else {
        target = new Date(eventDate);
      }
    }
  } else {
    target = new Date(eventDate);
  }

  if (!target || isNaN(target.getTime())) return Infinity;

  // Normalise both target and now to LOCAL midnight for whole-day math.
  target.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return Math.round((target - now) / 86400000);
};

